"use client";

import type { Alignment } from "@/app/_api/generateTts";
import type { Voice } from "@/app/_data/voices";
import type { FC } from "react";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { useSpinDelay } from "spin-delay";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

import { generateStreamingTts } from "@/app/_api/generateTts";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { getErrorStatusFromUnknownError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { Button } from "@ui/components/core/Button";
import { VoiceSelector } from "@ui/components/demos/TTS/components/TTSControls";
import { TextHighlighter } from "@ui/components/demos/TTS/TextHighlighter";
import { TTSModels } from "@mw/shared/models";

type PlayingState = "idle" | "loading" | "playing" | "paused";

export interface TranscriptionContentProps {
  /** The text to be converted to speech */
  text: string;
  /** Label displayed above the chapter title (e.g., "Chapter 1") */
  chapterLabel?: string;
  /** Chapter title displayed below the label */
  chapterTitle?: string;
  /** Book title (shown on mobile instead of chapter title) */
  bookTitle?: string;
  /** Author name (shown on mobile instead of chapter label) */
  author?: string;
  /** Available voices for selection */
  voices: Voice[];
  /** Currently selected voice */
  selectedVoice?: Voice;
  /** Callback when voice changes */
  onVoiceChange: (voice: Voice) => void;
  /** TTS model to use (defaults to ELEVEN_TURBO_V2_5) */
  model?: TTSModels;
  /** Background color class for gradients (defaults to tw-from-cream) */
  gradientFromColor?: string;
  /** Background color class for gradients via (defaults to tw-via-cream/80) */
  gradientViaColor?: string;
  /** Callback when quota exceeded error occurs */
  onQuotaExceeded?: () => void;
  /** Width class for the voice selector trigger (defaults to tw-w-60) */
  voiceSelectorWidthClassName?: string;
}

export const TranscriptionContent: FC<TranscriptionContentProps> = ({
  text,
  chapterLabel,
  chapterTitle,
  bookTitle,
  author,
  voices,
  selectedVoice,
  onVoiceChange,
  model = TTSModels.ELEVEN_TURBO_V2_5,
  gradientFromColor = "tw-from-cream",
  gradientViaColor = "tw-via-cream/80",
  onQuotaExceeded,
  voiceSelectorWidthClassName = "tw-w-56",
}) => {
  const t = useTranslations("ClientKeys.AudioBook");
  const tGlobals = useTranslations("ClientKeys.Globals");
  const [playingState, setPlayingState] = useState<PlayingState>("idle");
  const [alignment, setAlignment] = useState<Alignment | null>(null);
  const [needsRegeneration, setNeedsRegeneration] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { error, setError } = useAutoDismissError();

  const audioRef = useRef<HTMLAudioElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Mark as needing regeneration when voice changes
  useEffect(() => {
    setNeedsRegeneration(true);
  }, [selectedVoice?.voiceId]);

  // Sync playing state with audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setPlayingState("idle");
    const handlePlay = () => setPlayingState("playing");
    const handlePause = () => setPlayingState("paused");

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      cleanupRef.current?.();
    };
  }, []);

  // Track scroll position for top gradient visibility
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setHasScrolled(container.scrollTop > 0);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset scroll position and state when text changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }

    setAlignment(null);
    setPlayingState("idle");
    setNeedsRegeneration(true);
    setHasScrolled(false);
    abortControllerRef.current?.abort();
    cleanupRef.current?.();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [text]);

  const generate = useCallback(async () => {
    if (!audioRef.current || !selectedVoice || !text.trim()) return;

    setPlayingState("loading");
    setAlignment(null);

    // Abort any previous generation
    abortControllerRef.current?.abort();
    cleanupRef.current?.();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = await getCaptchaToken({ behaviourType: "tts" });

      const { cleanup, promise } = generateStreamingTts({
        voiceId: selectedVoice.voiceId,
        config: { text },
        modelId: model,
        audioElement: audioRef.current,
        speed: 1,
        hcaptcha_token: token,
        onTimestampsReceived: response => {
          if (response.alignment) {
            setAlignment(existingAlignment => {
              if (!existingAlignment) {
                return response.alignment;
              }
              if (!response.alignment) {
                return existingAlignment;
              }
              // Accumulate alignment chunks
              return {
                character_end_times_seconds: [
                  ...existingAlignment.character_end_times_seconds,
                  ...response.alignment.character_end_times_seconds,
                ],
                character_start_times_seconds: [
                  ...existingAlignment.character_start_times_seconds,
                  ...response.alignment.character_start_times_seconds,
                ],
                characters: [...existingAlignment.characters, ...response.alignment.characters],
              };
            });
          }
        },
        abortController,
      });

      cleanupRef.current = cleanup;
      await promise;
      setNeedsRegeneration(false);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      logger.error(err instanceof Error ? err : null, "TTS generation error");

      const errorStatus = getErrorStatusFromUnknownError(err);
      if (errorStatus === "quota_exceeded") {
        onQuotaExceeded?.();
      } else {
        setError(t("generation_error"));
      }
      setPlayingState("idle");
    }
  }, [selectedVoice, text, model, setError, t, onQuotaExceeded]);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    switch (playingState) {
      case "idle":
        if (needsRegeneration) {
          generate();
        } else {
          audio.play();
        }
        break;
      case "playing":
        audio.pause();
        break;
      case "paused":
        if (needsRegeneration) {
          generate();
        } else {
          audio.play();
        }
        break;
    }
  }, [playingState, needsRegeneration, generate]);

  const handleCharacterClick = useCallback(
    (characterIndex: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      // Find the start time for this character from alignment
      const startTime = alignment?.character_start_times_seconds[characterIndex];
      if (startTime === undefined) return;

      audio.currentTime = startTime;
      if (audio.paused) {
        audio.play();
      }
    },
    [alignment],
  );

  const handleVoiceChange = useCallback(
    (voice: Voice) => {
      onVoiceChange(voice);
      // Stop current playback when voice changes
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      setPlayingState("idle");
    },
    [onVoiceChange],
  );

  const isLoading = playingState === "loading";
  const isPlaying = playingState === "playing";
  const canPlay = !!selectedVoice && !!text.trim();
  const showLoading = useSpinDelay(isLoading, { delay: 200, minDuration: 500 });

  // Split text into characters for display (used when no alignment yet)
  const displayCharacters = text.split("");

  return (
    <div className='tw-flex-1 tw-min-h-0 tw-relative tw-flex tw-flex-col'>
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      {/* Top gradient overlay - appears when scrolled */}
      <div
        className={clsx(
          "tw-absolute -tw-top-1 -tw-left-6 md:-tw-left-24 -tw-right-6 md:-tw-right-24 tw-h-16 tw-pointer-events-none tw-bg-gradient-to-b tw-to-transparent tw-z-10 tw-rounded-t-2xl tw-transition-opacity tw-duration-200",
          gradientFromColor,
          hasScrolled ? "tw-opacity-100" : "tw-opacity-0",
        )}
      />

      {/* Transcript content with overlaid controls */}
      <div className='tw-flex-1 tw-min-h-0 tw-relative'>
        <div ref={scrollContainerRef} className='tw-h-full tw-overflow-y-auto tw-scrollbar-none'>
          {/* Header - scrolls with content */}
          <div className='tw-pb-6 lg:tw-pb-12'>
            {/* Single column header - book • author on first line, chapter title below */}
            {(bookTitle || author) && (
              <div className='lg:tw-hidden'>
                <span className='tw-block tw-type-2xs tw-text-gray-500 tw-mb-3'>
                  {bookTitle}
                  {bookTitle && author && " • "}
                  {author}
                </span>
                {chapterTitle && <span className='tw-block tw-text-3xl tw-text-gray-800'>{chapterTitle}</span>}
              </div>
            )}
            {/* Desktop header OR fallback when no bookTitle/author */}
            {(chapterLabel || chapterTitle) && (
              <div className={bookTitle || author ? "tw-hidden lg:tw-block" : undefined}>
                {chapterLabel && <span className='tw-block tw-type-2xs tw-text-gray-500 tw-mb-3'>{chapterLabel}</span>}
                {chapterTitle && <span className='tw-block tw-text-3xl tw-text-gray-800'>{chapterTitle}</span>}
              </div>
            )}
          </div>

          <m.div
            key={text}
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <TextHighlighter
              characters={alignment?.characters ?? []}
              displayCharacters={displayCharacters}
              startTimes={alignment?.character_start_times_seconds ?? []}
              endTimes={alignment?.character_end_times_seconds ?? []}
              mediaElement={audioRef.current}
              onClick={handleCharacterClick}
              className='tw-text-sm tw-leading-relaxed tw-text-gray-700 tw-pb-32'
              withBrackets={false}
            />
          </m.div>
        </div>

        {/* Gradient overlay with controls */}
        <div
          className={clsx(
            "tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-pointer-events-none tw-bg-gradient-to-t tw-to-transparent tw-pt-16 tw-pb-8",
            gradientFromColor,
            gradientViaColor,
          )}
        >
          <div className='tw-px-6 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-pointer-events-auto tw-h-11'>
            <AnimatePresence mode='wait' initial={false}>
              {error ? (
                <m.p
                  key='error'
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className='tw-text-center tw-text-sm tw-leading-snug tw-text-red-700'
                >
                  {error}
                </m.p>
              ) : (
                <m.div
                  key='controls'
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className='tw-flex tw-items-center tw-gap-3'
                >
                  <Button
                    variant='elevated'
                    size='pill'
                    onClick={handlePlayPause}
                    disabled={!canPlay}
                    aria-label={isPlaying ? tGlobals("pause") : tGlobals("play")}
                  >
                    <svg
                      viewBox='0 0 24 24'
                      aria-hidden
                      className={clsx("tw-size-4", showLoading && "tw-animate-spin")}
                      style={{ animationDuration: "0.7s" }}
                    >
                      {showLoading ? (
                        <g fill='none' stroke='currentColor' strokeWidth='2.25'>
                          <path strokeOpacity='.3' d='M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' />
                          <path strokeLinecap='round' d='M20.945 13A9.004 9.004 0 0 1 13 20.945' />
                        </g>
                      ) : isPlaying ? (
                        <path
                          fill='currentColor'
                          d='M6.75 3A2.75 2.75 0 0 0 4 5.75v12.5A2.75 2.75 0 0 0 6.75 21h.5A2.75 2.75 0 0 0 10 18.25V5.75A2.75 2.75 0 0 0 7.25 3zM16.75 3A2.75 2.75 0 0 0 14 5.75v12.5A2.75 2.75 0 0 0 16.75 21h.5A2.75 2.75 0 0 0 20 18.25V5.75A2.75 2.75 0 0 0 17.25 3z'
                        />
                      ) : (
                        <path
                          fill='currentColor'
                          d='M9.244 2.368C7.414 1.184 5 2.497 5 4.676v14.648c0 2.18 2.414 3.493 4.244 2.309l11.318-7.324c1.675-1.084 1.675-3.534 0-4.618z'
                        />
                      )}
                    </svg>
                  </Button>

                  <VoiceSelector
                    voices={voices}
                    selectedVoice={selectedVoice}
                    onVoiceChange={handleVoiceChange}
                    size='md'
                    variant='simple'
                    showSelectedAvatar={true}
                    triggerVariant='outline-new'
                    triggerClassName={clsx("tw-bg-white tw-h-11", voiceSelectorWidthClassName)}
                  />
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
